/*
  MyAttestations.jsx
  ----------------
  Interface pour afficher les attestations de l'utilisateur.
*/

import { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Group, Text, ActionIcon, Menu, Title, rem, Button, Modal, TextInput, Loader, Select, Grid, Divider } from '@mantine/core';
import { IconDots, IconFolderPlus, IconTrash, IconPlus, IconPencil, IconFile, IconArrowRight, IconFileTypePdf, IconFileText, IconFileSpreadsheet } from '@tabler/icons-react';
import { get, post } from '../tools/Request';
import { showNotification } from '@mantine/notifications';
import { useUser } from '../tools/Context';
import { useNavigate } from 'react-router-dom';

export default function MyAttestations() {
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [moveFileTarget, setMoveFileTarget] = useState(null);
  const [moveToFolderId, setMoveToFolderId] = useState(null);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const { refreshPage } = useUser();
  const [modalOpen, setModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [createInFolder, setCreateInFolder] = useState(null);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const groupId = localStorage.getItem('groupId');
  const groupName = localStorage.getItem('groupName');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshPage]);

  const fetchFolders = async () => {
    refreshPage(false);
    try {
      const res = await get('folders', undefined, localStorage.getItem('userToken'));
      setFolders(res);
    } catch (error) {
      showNotification({ title: 'Erreur', message: 'Impossible de charger les dossiers.', color: 'red' });
    }
  };

  const fetchFiles = async (folderId) => {
    setLoadingFiles(true);
    try {
      const res = await get(`files/by-folder/${folderId}`, undefined, localStorage.getItem('userToken'));
      setFolderFiles(res);
    } catch {
      showNotification({ title: 'Erreur', message: 'Erreur lors du chargement des fichiers.', color: 'red' });
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(folderId);
    fetchFiles(folderId);
  };

  const handleSubmitFolder = async () => {
    if (!newFolderName) {
      showNotification({ title: 'Erreur', message: 'Nom de dossier requis.', color: 'red' });
      return;
    }
    try {
      await post(
        'folders/create',
        {
          folderName: newFolderName,
          groupId: groupId === 'null' ? 0 : groupId,
          parent_id: createInFolder?.id || null
        },
        localStorage.getItem('userToken')
      );

      showNotification({ title: 'Succès', message: 'Dossier créé.', color: 'green' });
      setModalOpen(false);
      setNewFolderName('');
      fetchFolders();
    } catch (error) {
      showNotification({ title: 'Erreur', message: 'Erreur lors de la création.', color: 'red' });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await post(`folders/delete/${folderId}`, undefined, localStorage.getItem('userToken'));
      showNotification({ title: 'Supprimé', message: 'Dossier supprimé.', color: 'green' });
      fetchFolders();
    } catch (error) {
      showNotification({ title: 'Erreur', message: error?.error || 'Erreur', color: 'red' });
    }
  };

  const handleRenameFolder = async () => {
    if (!renameTarget || !renameValue) return;
    try {
      await post(`folders/rename/${renameTarget.id}`, { newName: renameValue }, localStorage.getItem('userToken'));
      showNotification({ title: 'Succès', message: 'Dossier renommé.', color: 'green' });
      setRenameModalOpen(false);
      fetchFolders();
    } catch (error) {
      showNotification({ title: 'Erreur', message: 'Impossible de renommer.', color: 'red' });
    }
  };

  const getFileStatus = (file) => {
    if (!file.blockhash) return { text: 'En attente', color: 'orange' };
    if (file.revoked) return { text: 'Révoquée', color: 'red' };
    return { text: 'Validée', color: 'green' };
  };

  const getFileIcon = (file) => {
    if (file.type?.startsWith('image/')) {
      return (
        <img
          src={`${process.env.REACT_APP_API_URL}api/tools/${file.filehash}?uid=${file.data_id}&inline=1`}
          alt={file.name}
          style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
        />
      );
    }
    if (file.type === 'pdf') return <IconFileTypePdf size={20} color="red" />;
    if (file.name?.match(/\.(doc|docx|txt)$/)) return <IconFileText size={20} color="blue" />;
    if (file.name?.match(/\.(xls|xlsx|csv)$/)) return <IconFileSpreadsheet size={20} color="green" />;
    return <IconFile size={20} />;
  };

  const BASE_INDENT = 20; // space per level
  const WIDTH_REDUCTION = 20; // width reduction per level

  const renderFolderTree = (parentId = null, level = 0) => {
    const items = folders.filter((f) => f.parent_id === parentId);

    return items.map((folder) => {
      const isSelected = selectedFolderId === folder.id;

      return (
        <Box
          key={folder.id}
          style={{
            marginLeft: rem(level * BASE_INDENT),
            width: `calc(100% - ${level * WIDTH_REDUCTION}px)`
          }}>
          <Group
            justify="space-between"
            px="sm"
            py={10}
            onClick={() => handleSelectFolder(folder.id)}
            style={{
              cursor: 'pointer',
              borderRadius: rem(10),
              border: '1px solid var(--mantine-color-default-border)',
              backgroundColor: isSelected ? 'var(--mantine-primary-color-light)' : 'var(--mantine-color-default)',
              boxShadow: isSelected ? 'inset 3px 0 0 var(--mantine-primary-color-filled)' : 'none',
              transition: 'background-color 120ms ease, border-color 120ms ease'
            }}
            onMouseEnter={(e) => {
              if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
            }}
            onMouseLeave={(e) => {
              if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--mantine-color-default)';
            }}>
            <Group gap="xs" wrap="nowrap">
              📁
              <Text size="sm" fw={isSelected ? 700 : 600} lineClamp={1}>
                {folder.foldername}
              </Text>
            </Group>

            <Menu shadow="md" width={190} position="bottom-end">
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
                  <IconDots size={16} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconFolderPlus size={14} />}
                  onClick={() => {
                    setCreateInFolder(folder);
                    setModalOpen(true);
                  }}>
                  Créer un sous-dossier
                </Menu.Item>

                <Menu.Item
                  leftSection={<IconPencil size={14} />}
                  onClick={() => {
                    setRenameTarget(folder);
                    setRenameValue(folder.foldername);
                    setRenameModalOpen(true);
                  }}>
                  Renommer
                </Menu.Item>

                <Menu.Item leftSection={<IconTrash size={14} />} color="red" onClick={() => handleDeleteFolder(folder.id)}>
                  Supprimer
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>

          {/* children */}
          <Box mt={6}>{renderFolderTree(folder.id, level + 1)}</Box>
        </Box>
      );
    });
  };

  const moveFolderOptions = useMemo(() => {
    // exclude current folder to prevent no-op moves
    return folders
      .filter((f) => f.id !== moveFileTarget?.folder_id)
      .map((f) => ({
        value: f.id,
        label: f.full_path ?? f.foldername
      }));
  }, [folders, moveFileTarget?.folder_id]);

  const openMoveModalForFile = (file) => {
    setMoveFileTarget(file);
    setMoveToFolderId(null);
    setMoveModalOpen(true);
  };

  const handleMoveFile = async () => {
    if (!moveFileTarget?.id) {
      showNotification({ title: 'Erreur', message: 'Fichier invalide.', color: 'red' });
      return;
    }
    if (!moveToFolderId) {
      showNotification({ title: 'Erreur', message: 'Choisissez un dossier de destination.', color: 'red' });
      return;
    }

    try {
      await post(`files/move/${moveFileTarget.id}`, { newFolderId: moveToFolderId }, localStorage.getItem('userToken'));

      showNotification({ title: 'Succès', message: 'Fichier déplacé.', color: 'green' });
      setMoveModalOpen(false);
      setMoveToFolderId(null);

      if (selectedFolderId) fetchFiles(selectedFolderId);
    } catch (e) {
      showNotification({
        title: 'Erreur',
        message: e?.details || e?.error || 'Erreur lors du déplacement.',
        color: 'red'
      });
    }
  };

  return (
    <Box p="lg">
      <Group justify="space-between" mb="md">
        <Box>
          <Title order={3}>Gérer mes documents</Title>
          {groupName && (
            <Text size="sm" c="dimmed">
              Groupe : {groupName}
            </Text>
          )}
        </Box>
      </Group>

      <Divider size="sm" mb={20} />

      <Grid>
        <Grid.Col span={4}>
          <Group justify="space-between" mb="sm">
            <Button
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                setCreateInFolder(null);
                setNewFolderName('');
                setModalOpen(true);
              }}>
              Nouveau dossier
            </Button>
          </Group>

          <Stack spacing="xs">{renderFolderTree()}</Stack>
        </Grid.Col>

        <Grid.Col span={8}>
          {selectedFolderId && (
            <Group justify="space-between" mb="sm">
              <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => navigate('/savedocument', { state: { selectedFolderId } })}>
                Sauvegarder un document
              </Button>
            </Group>
          )}

          {loadingFiles ? (
            <Loader size="sm" />
          ) : folderFiles.length === 0 ? (
            <Text size="sm" c="dimmed">
              Aucun fichier dans ce dossier.
            </Text>
          ) : (
            <Box style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <Stack spacing="sm">
                {folderFiles.map((file) => {
                  const url = `${process.env.REACT_APP_API_URL}api/tools/${file.filehash}?uid=${file.data_id}&inline=1`;
                  const isImage = ['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(file.type);
                  const status = getFileStatus(file);

                  return (
                    <Box
                      key={file.id}
                      p="sm"
                      style={{
                        border: '1px solid var(--mantine-color-default-border)',
                        backgroundColor: 'var(--mantine-color-default)',
                        borderRadius: rem(10),
                        boxShadow: 'var(--mantine-shadow-xs)'
                      }}>
                      <Group align="center" wrap="nowrap">
                        {isImage ? (
                          <img
                            src={url}
                            alt={file.name}
                            style={{
                              width: 85,
                              height: 85,
                              objectFit: 'cover',
                              borderRadius: 8,
                              flexShrink: 0
                            }}
                          />
                        ) : (
                          getFileIcon(file)
                        )}

                        <Divider orientation="vertical" />

                        <Group justify="space-between" align="flex-start" style={{ flex: 1 }} wrap="nowrap">
                          <Stack gap={6}>
                            <Box style={{ flex: 1 }}>
                              <Text fw={600}>{file.name}</Text>
                              <Text size="xs" c={status.color}>
                                Attestation {status.text}
                              </Text>
                            </Box>

                            <Group gap="xs">
                              <Button
                                variant="light"
                                color="gray"
                                size="xs"
                                onClick={() => window.open(`${process.env.REACT_APP_API_URL}api/tools/${file.filehash}?uid=${file.data_id}`, '_blank')}>
                                Télécharger le fichier
                              </Button>

                              <Button
                                variant="light"
                                color="teal"
                                size="xs"
                                onClick={() => {
                                  window.open(`/proof?uid=${file.data_id}`, '_blank');
                                }}>
                                Voir la preuve
                              </Button>
                            </Group>
                          </Stack>

                          <ActionIcon size="md" color="blue" variant="light" onClick={() => openMoveModalForFile(file)}>
                            <IconArrowRight size={16} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Grid.Col>
      </Grid>

      {/* Create folder modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title="Créer un nouveau dossier" centered>
        <Stack>
          <TextInput label="Nom du dossier" value={newFolderName} onChange={(e) => setNewFolderName(e.currentTarget.value)} />
          <Button onClick={handleSubmitFolder}>Créer</Button>
        </Stack>
      </Modal>

      {/* Rename folder modal */}
      <Modal opened={renameModalOpen} onClose={() => setRenameModalOpen(false)} title="Renommer le dossier" centered>
        <Stack>
          <TextInput label="Nouveau nom" value={renameValue} onChange={(e) => setRenameValue(e.currentTarget.value)} />
          <Button onClick={handleRenameFolder}>Renommer</Button>
        </Stack>
      </Modal>

      {/* Move file modal */}
      <Modal
        opened={moveModalOpen}
        onClose={() => {
          setMoveModalOpen(false);
          setMoveToFolderId(null);
        }}
        title="Déplacer le fichier"
        centered>
        <Stack>
          <Select label="Déplacer vers" placeholder="Sélectionnez un dossier" data={moveFolderOptions} value={moveToFolderId} onChange={setMoveToFolderId} searchable clearable />

          <Button disabled={!moveToFolderId} onClick={handleMoveFile}>
            Déplacer
          </Button>
        </Stack>
      </Modal>
    </Box>
  );
}
